import React, { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  IoCalendarNumber,
  IoBriefcase,
  IoSpeedometer,
  IoTicket,
  IoTime,
  IoBarChart,
  IoNotifications,
  IoLockClosed,
  IoLockOpen
} from "react-icons/io5";
import moment from "moment";
import Select from "react-select";

import * as supportTicketActions from "../../actions/supportTicketActions";
import * as callActions from "../../actions/callActions";
import * as userActions from "../../actions/userActions";
import * as departmentActions from "../../actions/departmentActions";
import * as codeActions from "../../actions/codeActions";
import * as ptoRequestsActions from "../../actions/ptoRequestActions";
import * as projectActions from "../../actions/projectActions";

import { subtractMonths, subtractWeeks } from "../../helpers/date";

import UserSelect from "../common/UserSelect";
import DepartmentSelect from "../common/DepartmentSelect";

import DashboardProjects from "./Projects/Projects";
import DashboardNotificationCenter from "./DashboardNotificationCenter";
import DashboardMetrics from "./DashboardMetrics";
import DashboardCallStatus from "./DashboardCallStatus";
import Calendar from "./calendar/Calendar";
import DashboardTickets from "./DashboardTickets";
import DashboardAggregateHours from "./DashboardAggregateHours";
import SupportTicketsWidget from "./SupportTicketsWidget";

import GridLayout from "../common/gridLayout/Gridlayout";

import "./Dashboard.scss";

const getViewOptions = () => {
  const getToday = (isTicket = false) => ({
    start: moment().startOf("day").toDate().getTime(),
    end: isTicket ? null : moment().toDate().getTime()
  });
  const getThisWeek = (isTicket = false) => ({
    start: moment().startOf("week").toDate().getTime(),
    end: isTicket ? null : moment().endOf("week").toDate().getTime()
  });

  const getLastWeek = (isTicket = false) => {
    const lastWeek = subtractWeeks(new Date(), 1);
    return {
      start: moment(lastWeek).startOf("week").toDate().getTime(),
      end: isTicket ? null : moment().endOf("week").toDate().getTime()
    };
  };

  const getLastMonth = (isTicket = false) => {
    const lastMonth = subtractMonths(new Date(), 1);

    return {
      start: moment(lastMonth).startOf("month").toDate().getTime(),
      end: isTicket ? null : moment().endOf("week").toDate().getTime()
    };
  };

  const getAllTime = () => ({});

  return [
    { value: "today", label: "Today", getDateRange: getToday },
    { value: "thisWeek", label: "This Week", getDateRange: getThisWeek },
    { value: "lastWeek", label: "Last Week", getDateRange: getLastWeek },
    { value: "lastMonth", label: "Last Month", getDateRange: getLastMonth },
    {
      value: "allTime",
      label: "All Time",
      getDateRange: getAllTime
    }
  ];
};

const DEFAULT_LAYOUTS = {
  lg: [
    { x: 0, y: 0, w: 3, h: 6, i: "C1" },
    { x: 3, y: 0, w: 3, h: 6, i: "C2" },
    { x: 6, y: 0, w: 6, h: 6, i: "C3" },
    { x: 0, y: 2, w: 12, h: 8, i: "C5" },
    { x: 0, y: 3, w: 12, h: 4, i: "C6" },
    { x: 0, y: 4, w: 12, h: 13, i: "C7" },
    { x: 0, y: 14, w: 12, h: 13, i: "C8" }
  ]
};

const DEFAULT_TOOLBOX = {
  lg: [
    { x: 0, y: 0, w: 3, h: 6, i: "C1" },
    { x: 3, y: 0, w: 3, h: 6, i: "C2" },
    { x: 6, y: 0, w: 6, h: 6, i: "C3" },
    { x: 0, y: 1, w: 12, h: 11, i: "C4" },
    { x: 0, y: 2, w: 12, h: 8, i: "C5" },
    { x: 0, y: 3, w: 12, h: 4, i: "C6" },
    { x: 0, y: 4, w: 12, h: 13, i: "C7" },
    { x: 0, y: 14, w: 12, h: 13, i: "C8" }
  ]
};

const Dashboard = ({
  isMobile,
  auth,
  actions,
  calls,
  codes,
  ptoRequests,
  innerWidth,
  projects
}) => {
  const [state, setState] = useState({
    viewOptions: getViewOptions(),
    activeView: getViewOptions().find((view) => view.value === "thisWeek"),
    activeViewLabel: "",
    activeRange: {},
    user: {},
    department: {},
    needDashboardUpdate: false,
    isDragUnlocked: false
  });

  const [heightChange, setHeightChange] = useState({});

  const [dragState, setDragState] = useState({
    dragInProcess: false,
    dragOver: true
  });

  const [scrollState, setScrollState] = useState({
    scrollY: 0
  });

  const history = useHistory();

  const onLayoutChange = (layout, layoutsArr) => {
    if (state.needDashboardUpdate) {
      const data = {
        ...auth.user,
        dashboard_layout: layoutsArr
      };
      actions.updateUser(data, "layout");

      auth.user.dashboard_layout = layoutsArr;
      setState((prev) => ({
        ...prev,
        needDashboardUpdate: false
      }));
    }
  };

  const onAddOrRemoveWidget = (newLayouts) => {
    const data = {
      ...auth.user,
      dashboard_layout: newLayouts
    };
    actions.updateUser(data, "layout");

    auth.user.dashboard_layout = newLayouts;
  };

  const onDrag = () => {
    if (!dragState.dragInProcess && state.isDragUnlocked) {
      setDragState({
        dragInProcess: true,
        dragOver: false
      });
      setState((prev) => ({
        ...prev,
        needDashboardUpdate: true
      }));
    }
  };

  const onDragStop = () => {
    setTimeout(() => {
      setDragState({
        dragInProcess: false,
        dragOver: true
      });
    }, 500);
  };

  const handleLockToggle = () => {
    setState({
      ...state,
      isDragUnlocked: !state.isDragUnlocked
    });
  };

  const layouts = useMemo(() => {
    if (auth.user.dashboard_layout?.lg?.length) {
      return { lg: auth.user.dashboard_layout.lg };
    }
    return DEFAULT_LAYOUTS;
  }, [auth.user.dashboard_layout]);

  const toolbox = useMemo(
    () => ({
      lg: DEFAULT_TOOLBOX.lg.filter((t) => !layouts.lg.find((l) => l.i === t.i))
    }),
    [layouts]
  );

  const components = useMemo(() => {
    const initialComponents = [
      {
        i: "C1",
        title: "Notification Center",
        icon: <IoNotifications />,
        component: <DashboardNotificationCenter range={state.activeRange} />,
        style: { height: 270 }
      },
      {
        i: "C2",
        title: "Metrics",
        icon: <IoSpeedometer />,
        component: (
          <DashboardMetrics
            calls={calls.calls || []}
            isDark={auth.isNightMode}
          />
        ),
        style: { height: 270 }
      },
      {
        i: "C3",
        title: "Call Status",
        icon: <IoTime />,
        component: (
          <DashboardCallStatus
            auth={auth}
            userId={
              state.user && state.user.value ? state.user.value : undefined
            }
            user={state.user}
            department={state.department}
            departmentId={
              state.department && state.department.value
                ? state.department.value
                : undefined
            }
            codes={codes}
            history={history}
            range={state.activeView.getDateRange()}
            isMobile={isMobile}
            dragState={dragState}
          />
        ),
        style: { height: 270 }
      },
      {
        i: "C4",
        title: "Calendar",
        icon: <IoCalendarNumber />,
        component: (
          <Calendar
            codes={codes}
            getPTORequests={actions.loadPtoRequests}
            data={ptoRequests}
            isNightMode={auth.isNightMode}
            user={state.user?.value || ""}
            department={state.department?.value || ""}
            dragState={dragState}
          />
        ),
        style: {}
      },
      {
        i: "C5",
        title: "My Tickets",
        icon: <IoTicket />,
        component: (
          <DashboardTickets
            auth={auth}
            codes={codes}
            user={state.user}
            userId={
              state.user && state.user.value ? state.user.value : undefined
            }
            isMobile={isMobile}
            departmentId={
              state.department && state.department.value
                ? state.department.value
                : undefined
            }
            history={history}
            range={state.activeView.getDateRange(true)}
            dragState={dragState}
          />
        ),
        style: {}
      },
      {
        i: "C6",
        title: "Call Breakdown",
        icon: <IoBarChart />,
        component: (
          <DashboardAggregateHours
            auth={auth}
            userId={
              state.user && state.user.value ? state.user.value : undefined
            }
            departmentId={
              state.department && state.department.value
                ? state.department.value
                : undefined
            }
            history={history}
            range={state.activeView.getDateRange()}
            onFetchCalls={(c) =>
              setHeightChange({
                changed: true,
                /**
                 * Math.ceil(c / 4) -> Gives us one unit every 4 elements
                 * Math.floor((c / 10) % 10) -> Gives us the second digit right * from left
                 * Example: c = 14
                 * Math.ceil(14 / 4) = 4
                 * Math.floor((14 / 10) % 10) = 1
                 * We should increase the size in: 4 + 14 + 1 + 2 = 20
                 *
                 * Special handle for c = 4 where this formula does not create  * enough spacing between components
                 */
                height: (() => {
                  switch (c) {
                    case 4:
                      return 8;
                    default:
                      return (
                        Math.ceil(c / 4) + c + Math.floor((c / 10) % 10) + 2
                      );
                  }
                })(),
                key: "C6"
              })
            }
            dragState={dragState}
          />
        ),
        style: {}
      },
      {
        i: "C7",
        title: "My Projects",
        icon: <IoBriefcase />,
        component: (
          <DashboardProjects
            maxListHeight="400px"
            auth={auth}
            isMobile={isMobile}
            projects={projects}
            dragState={dragState}
            getProjects={actions.getProjects}
          />
        ),
        style: { height: 270 }
      }
    ];
    if (auth.maxRole >= 5000) {
      initialComponents.push({
        i: "C8",
        title: "Support Tickets",
        icon: <IoTicket />,
        component: <SupportTicketsWidget history={history} />,
        style: { height: 400 }
      });
    }
    return initialComponents;
  }, [
    auth,
    calls,
    codes,
    history,
    isMobile,
    state,
    actions.loadPtoRequests,
    actions.getProjects,
    ptoRequests,
    dragState,
    projects
  ]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      user: { value: auth.user._id, label: auth.user.full_name },
      department: {
        value: auth.user.department._id,
        label: auth.user.department.description
      }
    }));
  }, [auth.user]);

  useEffect(() => {
    const fetchInitialData = async () => {
      actions.loadCalls({
        isPopulated: true,
        filtered: {
          assigned_to: [auth.user._id],
          arrival_date: moment().startOf("isoWeek"),
          completed_date: moment().endOf("isoWeek")
        }
      });
      actions.loadCodes();
    };
    fetchInitialData();
  }, [actions, auth.user]);

  useEffect(() => {
    if (innerWidth < 1045) {
      setHeightChange({
        changed: true,
        height: 15,
        key: "C5"
      });
    } else {
      setHeightChange({
        changed: true,
        height: 8,
        key: "C5"
      });
    }
  }, [innerWidth]);

  useEffect(() => {
    const onScroll = () => setScrollState({ scrollY: window.scrollY });
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const baseActions = useMemo(
    () => [
      {
        type: "select",
        component: function SelectAction() {
          return (
            <Select
              className="react-select-container"
              classNamePrefix="react-select-container"
              value={state.activeView}
              options={
                !state.user && !state.department
                  ? state.viewOptions.filter((v) => v.value !== "allTime")
                  : state.viewOptions
              }
              onChange={(view) =>
                setState((prevState) => ({
                  ...prevState,
                  activeView: view,
                  activeViewLabel: view.text,
                  activeRange: view.getDateRange()
                }))
              }
            />
          );
        }
      },
      {
        action: () => history.push(`/app/timesheet`),
        text: "Timesheet"
      },
      {
        action: () => history.push(`/app/calls/add`),
        text: "Add Call",
        className: "btn-success"
      }
    ],
    [history, state]
  );

  const headerActions = useMemo(
    () =>
      auth.maxRole >= 5000
        ? [
            {
              type: "select",
              component: function UserSelectAction() {
                return (
                  <UserSelect
                    value={state.user}
                    onChange={(user) =>
                      setState((prevState) => ({
                        ...prevState,
                        user,
                        activeView: user
                          ? state.activeView
                          : state.viewOptions.find(
                              (view) => view.value === "thisWeek"
                            ),
                        department: undefined
                      }))
                    }
                  />
                );
              }
            },
            {
              type: "select",
              component: function DepartmentSelectAction() {
                return (
                  <DepartmentSelect
                    value={state.department}
                    onChange={(department) =>
                      setState((prevState) => ({
                        ...prevState,
                        department,
                        activeView: department
                          ? state.activeView
                          : state.viewOptions.find(
                              (view) => view.value === "thisWeek"
                            ),
                        user: undefined
                      }))
                    }
                  />
                );
              }
            },
            ...baseActions
          ]
        : baseActions,
    [
      auth,
      state.department,
      state.user,
      state.activeView,
      state.viewOptions,
      baseActions
    ]
  );

  const isDraggable = isMobile ? state.isDragUnlocked : true;

  return (
    <div className="dashboard">
      {isMobile ? (
        <div className="lock-container">
          <button
            className="btn"
            style={{ backgroundColor: "transparent" }}
            type="button"
            onClick={() => handleLockToggle()}
          >
            {state.isDragUnlocked ? (
              <IoLockOpen
                style={{
                  width: "2rem",
                  height: "2rem",
                  color: "#50accb"
                }}
              />
            ) : (
              <IoLockClosed
                style={{
                  width: "2rem",
                  height: "2rem",
                  color: "#50accb"
                }}
              />
            )}
          </button>
        </div>
      ) : null}

      <GridLayout
        auth={auth}
        onAddOrRemoveWidget={onAddOrRemoveWidget}
        onLayoutChange={onLayoutChange}
        onDrag={onDrag}
        onDragStop={onDragStop}
        headerActions={headerActions}
        heightChange={heightChange}
        isMobile={isMobile}
        scrollState={scrollState}
        isDraggable={isDraggable}
        _toolbox={toolbox}
        _components={components}
        _layouts={layouts}
      />
    </div>
  );
};

Dashboard.propTypes = {
  actions: PropTypes.object,
  auth: PropTypes.object,
  calls: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  codes: PropTypes.array,
  isMobile: PropTypes.bool,
  ptoRequests: PropTypes.array,
  innerWidth: PropTypes.number,
  projects: PropTypes.object
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    codes: state.reducers.codes,
    ptoRequests: state.reducers.ptoRequests,
    calls: state.reducers.calls,
    projects: state.reducers.paginatedProjects
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...callActions,
        ...userActions,
        ...supportTicketActions,
        ...departmentActions,
        ...codeActions,
        ...ptoRequestsActions,
        ...projectActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(Dashboard);
